// Ukázky použití: navržené samolepky zákazníka automaticky vložené do fotek předmětů.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { LabelConfig, CustomBackground, CustomShape, CustomTemplate } from '../types';
import { generateLabelSvg } from '../lib/svgGenerator';
import { useFontsVersion } from '../lib/fontsVersion';
import { scenesForSite, visibleStickers, pickForSpots, layoutSpot, UsageScene, StickerPick, WARP_MARGIN } from '../lib/usageScenes';

interface LibProps {
  customBackgrounds?: CustomBackground[];
  customShapes?: CustomShape[];
  customTemplates?: CustomTemplate[];
}

let uid = 0;

const SpotSticker: React.FC<LibProps & { config: LabelConfig; scene: UsageScene; spotIdx: number; pick: StickerPick }> = ({ config, scene, spotIdx, pick, customBackgrounds, customShapes, customTemplates }) => {
  const spot = scene.spots[spotIdx];
  const id = useMemo(() => `us${++uid}`, []);
  const L = layoutSpot(spot, pick.w, pick.h, scene.w, scene.h);
  const svg = generateLabelSvg(config, pick.variant, 0, 0, pick.w, pick.h, false, customBackgrounds, customShapes, `${id}-${scene.id}`, false, customTemplates, 0, false);
  // mapa světla patří celé prázdné samolepce na fotce; samolepka zabírá její vystředěnou část
  const uw = Math.min(L.uw, 1), vh = Math.min(L.vh, 1); // přerostlá samolepka: mapu světla roztáhnout přes celou
  const fw = pick.w / uw, fh = pick.h / vh;
  const fx = -(1 - uw) / 2 * fw, fy = -(1 - vh) / 2 * fh;
  // Aby samolepka nepůsobila jako ostrá vektorová nálepka "přes" fotku: rozostření podle ostrosti fotky,
  // matný papír (menší kontrast) a zrno snímku. Vše v jednotkách samolepky (mm).
  const mm = 1 / Math.max(L.pxPerMm, 0.01); // 1 px fotky v mm
  const blur = (spot.blur ?? 0.6) * mm;
  const grain = (0.55 / mm).toFixed(3);
  // Ohyb po tvaru předmětu (válec láhve, kopule helmy) a zvlnění podle nerovností podkladu:
  // mapa posunu je narovnaná na celé místo na fotce včetně okraje, aby posun nesahal mimo mapu.
  // místo na fotce rozšířené o okraj – v něm leží mapa posunu, textura i rozšířená mapa světla
  const px = fx - WARP_MARGIN.x * fw, py = fy - WARP_MARGIN.y * fh;
  const pw = fw * (1 + 2 * WARP_MARGIN.x), ph = fh * (1 + 2 * WARP_MARGIN.y);
  const r = `x="${px.toFixed(3)}" y="${py.toFixed(3)}" width="${pw.toFixed(3)}" height="${ph.toFixed(3)}"`;
  const warp = spot.warp && spot.warpAmp ? (() => {
    return `<filter id="${id}-w" filterUnits="userSpaceOnUse" primitiveUnits="userSpaceOnUse" ${r} color-interpolation-filters="sRGB">`
      + `<feImage href="${spot.warp}" ${r} preserveAspectRatio="none" result="map"/>`
      + `<feDisplacementMap in="SourceGraphic" in2="map" scale="${(spot.warpAmp! * fh).toFixed(4)}" xChannelSelector="R" yChannelSelector="G"/>`
      + `<feGaussianBlur stdDeviation="${(0.35 * mm).toFixed(4)}"/></filter>`; // vyhladit schody posunu
  })() : '';
  // stín pod hranou: obrys samolepky rozmazaný a posunutý dolů (světlo shora), pod samolepkou
  const es = spot.edgeShadow || 0;
  const edge = es > 0
    ? `<filter id="${id}-e" x="-10%" y="-10%" width="120%" height="130%"><feGaussianBlur in="SourceAlpha" stdDeviation="${(0.9 * mm).toFixed(4)}"/>`
      + `<feOffset dy="${(0.9 * mm).toFixed(4)}"/><feComponentTransfer><feFuncA type="linear" slope="${es}"/></feComponentTransfer></filter>`
    : '';
  const html = `<defs>${warp}${edge}`
    + `<filter id="${id}-f" x="-5%" y="-5%" width="110%" height="110%" color-interpolation-filters="sRGB">`
    + `<feGaussianBlur stdDeviation="${blur.toFixed(4)}"/>`
    + `<feComponentTransfer><feFuncR type="linear" slope="0.9" intercept="0.04"/><feFuncG type="linear" slope="0.9" intercept="0.04"/><feFuncB type="linear" slope="0.9" intercept="0.035"/></feComponentTransfer>`
    + `<feColorMatrix type="saturate" values="0.9"/></filter>`
    + `<filter id="${id}-n" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency="${grain}" numOctaves="2" seed="7"/><feColorMatrix type="saturate" values="0"/></filter>`
    + `<mask id="${id}-m" maskUnits="userSpaceOnUse" ${r} style="mask-type:alpha"><use href="#${id}-s"/></mask></defs>`
    + (edge ? `<use href="#${id}-s" filter="url(#${id}-e)"/>` : '')
    + `<g id="${id}-s"${warp ? ` filter="url(#${id}-w)"` : ''}><g filter="url(#${id}-f)">${svg}</g></g>`
    + (spot.light || spot.shadePad
      ? `<image href="${spot.light || spot.shadePad}" ${r} preserveAspectRatio="none" mask="url(#${id}-m)" style="mix-blend-mode:multiply"/>`
      : `<image href="${spot.shade}" x="${fx}" y="${fy}" width="${fw}" height="${fh}" preserveAspectRatio="none" mask="url(#${id}-m)" style="mix-blend-mode:multiply"/>`)
    // textura podkladu (vlákna, vazba látky, zrno papíru): tmavší místa ztmaví, světlá zesvětlí
    + (spot.relief ? `<image href="${spot.relief}" ${r} preserveAspectRatio="none" mask="url(#${id}-m)" style="mix-blend-mode:hard-light"/>` : '')
    // odlesky lesklého povrchu (helma) – samolepka s lesklou laminací je přebírá
    + (spot.gloss ? `<image href="${spot.gloss}" ${r} preserveAspectRatio="none" mask="url(#${id}-m)" opacity="${spot.glossOpacity ?? 0.5}" style="mix-blend-mode:screen"/>` : '')
    + `<rect ${r} filter="url(#${id}-n)" mask="url(#${id}-m)" opacity="0.13" style="mix-blend-mode:overlay"/>`;
  return (
    <div className="rd-usage-spot" style={{ width: L.boxW, height: L.boxH, transform: L.matrix }}>
      <svg viewBox={`0 0 ${pick.w} ${pick.h}`} width={L.boxW} height={L.boxH} aria-hidden="true" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

/** Jedna fotka se samolepkami zákazníka. */
export const UsageSceneView: React.FC<LibProps & { config: LabelConfig; scene: UsageScene; eager?: boolean }> = ({ config, scene, eager, ...lib }) => {
  useFontsVersion();
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const upd = () => setScale(el.clientWidth / scene.w);
    upd();
    const ro = new ResizeObserver(upd); ro.observe(el);
    return () => ro.disconnect();
  }, [scene.w]);
  const stickers = visibleStickers(config);
  const picks = pickForSpots(scene.spots, stickers);
  return (
    <div ref={ref} className="rd-usage-photo" style={{ aspectRatio: `${scene.w} / ${scene.h}` }}>
      <img src={scene.img} alt={scene.title} loading={eager ? 'eager' : 'lazy'} draggable={false} />
      {scale > 0 && (
        <div className="rd-usage-stage" style={{ width: scene.w, height: scene.h, transform: `scale(${scale})` }}>
          {picks.map((p, i) => p && <SpotSticker key={i} config={config} scene={scene} spotIdx={i} pick={p} {...lib} />)}
        </div>
      )}
    </div>
  );
};

/** Pruh ukázek (v panelu 3 fotky, na stránce přes celou šířku až 6 fotek) a okno se všemi fotkami. */
const UsagePhotos: React.FC<LibProps & { config: LabelConfig; variant?: 'panel' | 'page' }> = ({ config, variant = 'panel', ...lib }) => {
  const scenes = useMemo(() => scenesForSite(), []);
  const [open, setOpen] = useState<number | null>(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    if (open === null && !all) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(null); setAll(false); }
      if (open !== null && e.key === 'ArrowRight') setOpen((open + 1) % scenes.length);
      if (open !== null && e.key === 'ArrowLeft') setOpen((open - 1 + scenes.length) % scenes.length);
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, all, scenes.length]);
  if (!scenes.length || !visibleStickers(config).length) return null;
  const isPage = variant === 'page';
  const preview = scenes.slice(0, isPage ? 6 : 3);
  const allBtn = <button type="button" className={`rd-btn-secondary rd-usage-all${isPage ? ' rd-usage-all--head' : ''}`} onClick={() => setAll(true)}><span>Zobrazit všechny ukázky</span> <span>({scenes.length})</span></button>;
  return (
    <section className={`rd-usage${isPage ? ' rd-usage--page no-print' : ''}`} aria-label="Jak to vypadá v praxi">
      <div className="rd-preview-head">
        <div>
          <h2 className="rd-h">Jak to vypadá v praxi</h2>
          <p className="rd-sub">Vaše samolepky na věcech, kam se nejčastěji lepí</p>
        </div>
        {isPage && <div className="ml-auto rd-usage-head-all">{allBtn}</div>}
      </div>
      <div className="rd-usage-strip">
        {preview.map((s, i) => (
          <button key={s.id} type="button" className="rd-usage-thumb" onClick={() => setOpen(i)} aria-label={s.title}>
            <UsageSceneView config={config} scene={s} eager {...lib} />
            <span>{s.title}</span>
          </button>
        ))}
      </div>
      {isPage ? <div className="rd-usage-foot-all">{allBtn}</div> : allBtn}

      {(all || open !== null) && createPortal(
        <div className="rd-sheet-modal" role="dialog" aria-modal="true" aria-label="Ukázky použití" onClick={() => { setOpen(null); setAll(false); }}>
          <div className="rd-sheet-modal-box rd-usage-modal" onClick={e => e.stopPropagation()}>
            <div className="rd-preview-head">
              <div>
                <h2 className="rd-h">{open !== null ? scenes[open].title : 'Jak to vypadá v praxi'}</h2>
                <p className="rd-sub">{open !== null ? scenes[open].hint : 'Ukázky se mění podle toho, co právě navrhujete. Barvy na fotce jsou orientační.'}</p>
              </div>
              <div className="ml-auto flex gap-2">
                {open !== null && all && <button type="button" className="rd-btn-ghost" onClick={() => setOpen(null)}>Všechny ukázky</button>}
                <button type="button" className="rd-btn-secondary" onClick={() => { setOpen(null); setAll(false); }}>Zavřít</button>
              </div>
            </div>
            {open !== null ? (
              <div className="rd-usage-one">
                <UsageSceneView config={config} scene={scenes[open]} eager {...lib} />
                <div className="rd-usage-nav">
                  <button type="button" className="rd-btn-secondary" onClick={() => setOpen((open - 1 + scenes.length) % scenes.length)} aria-label="Předchozí ukázka"><span aria-hidden="true">‹</span> <span>Předchozí</span></button>
                  <span>{open + 1} / {scenes.length}</span>
                  <button type="button" className="rd-btn-secondary" onClick={() => setOpen((open + 1) % scenes.length)} aria-label="Další ukázka"><span>Další</span> <span aria-hidden="true">›</span></button>
                </div>
              </div>
            ) : (
              <div className="rd-usage-grid">
                {scenes.map((s, i) => (
                  <button key={s.id} type="button" className="rd-usage-thumb" onClick={() => setOpen(i)}>
                    <UsageSceneView config={config} scene={s} {...lib} />
                    <span>{s.title}</span>
                    <small>{s.hint}</small>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>, document.body)}
    </section>
  );
};

export default UsagePhotos;
